import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { apiUrl, fetchWithAuth } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface CostpointWithoutTags {
  costpoint_assignment_id: number;
  transaction_id: number;
  transaction_date: string;
  transaction_description: string;
  transaction_amount: number;
  costpoint_name: string;
  allocated_amount: number;
}

interface BudgetItemWithoutTags {
  budget_item_id: number;
  budget_period_id: number;
  year: number;
  period_type: 'H1' | 'H2' | 'ANNUAL';
  period_status: 'draft' | 'active' | 'locked' | 'archived';
  costpoint: string;
  type: 'income' | 'expenses';
  amount: number;
  description: string;
}

interface Tag {
  id: number;
  name: string;
  display_name: string;
}

const PERIOD_STATUS_BADGE_STYLES: Record<BudgetItemWithoutTags['period_status'], string> = {
  draft: 'bg-gray-100 text-gray-800',
  active: 'bg-green-100 text-green-800',
  locked: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-200 text-gray-600',
};

// Only admins and budget managers (budget_creator) can view/fix budget items missing tags -
// deliberately excludes operator, which sits at the same role-hierarchy level as budget_creator
// but isn't what "budget manager" refers to here. Mirrors the backend's own exact-membership
// check in diagnostics.controller.ts (not a hierarchy check) - this is just the frontend's
// show/hide nicety on top of that real enforcement.
const BUDGET_MANAGER_ROLES = ['admin', 'superadmin', 'budget_creator'];

const MissingTags: React.FC = () => {
  const { user } = useAuth();
  const canManageBudgetTags = !!user && BUDGET_MANAGER_ROLES.includes(user.role);

  const [data, setData] = useState<CostpointWithoutTags[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  // Separate state for the budget-items section so the two tables' "currently editing" rows
  // never interfere with each other.
  const [budgetItemsData, setBudgetItemsData] = useState<BudgetItemWithoutTags[]>([]);
  const [budgetItemsLoading, setBudgetItemsLoading] = useState(false);
  const [budgetItemsError, setBudgetItemsError] = useState<string | null>(null);
  const [editingBudgetItemRow, setEditingBudgetItemRow] = useState<number | null>(null);
  const [selectedBudgetItemTags, setSelectedBudgetItemTags] = useState<number[]>([]);
  const [savingBudgetItemTags, setSavingBudgetItemTags] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(apiUrl('diagnostics/missing-tags'));
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await fetchWithAuth(apiUrl('tags'));
      const result = await response.json();
      if (result.success) {
        setTags(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  }, []);

  const fetchBudgetItemsData = useCallback(async () => {
    try {
      setBudgetItemsLoading(true);
      setBudgetItemsError(null);
      const response = await fetchWithAuth(apiUrl('diagnostics/missing-tags/budget-items'));
      const result = await response.json();

      if (result.success) {
        setBudgetItemsData(result.data);
      } else {
        setBudgetItemsError('Failed to fetch data');
      }
    } catch (err) {
      setBudgetItemsError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setBudgetItemsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchTags();
  }, [fetchTags, fetchData]);

  useEffect(() => {
    if (canManageBudgetTags) {
      fetchBudgetItemsData();
    }
  }, [canManageBudgetTags, fetchBudgetItemsData]);

  const handleEditRow = (assignmentId: number) => {
    setEditingRow(assignmentId);
    setSelectedTags([]);
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setSelectedTags([]);
  };

  const handleSaveTags = async (assignmentId: number) => {
    if (selectedTags.length === 0) {
      toast.warning('Please select at least one tag');
      return;
    }

    try {
      setSaving(true);
      const response = await fetchWithAuth(
        apiUrl(`diagnostics/costpoint-assignments/${assignmentId}/tags`),
        {
          method: 'POST',
          body: JSON.stringify({ tagIds: selectedTags }),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(result.message);
        setEditingRow(null);
        setSelectedTags([]);
        // Refresh the data to remove the fixed item
        await fetchData();
      } else {
        toast.error('Failed to add tags');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add tags');
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId: number) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleEditBudgetItemRow = (budgetItemId: number) => {
    setEditingBudgetItemRow(budgetItemId);
    setSelectedBudgetItemTags([]);
  };

  const handleCancelBudgetItemEdit = () => {
    setEditingBudgetItemRow(null);
    setSelectedBudgetItemTags([]);
  };

  const toggleBudgetItemTag = (tagId: number) => {
    setSelectedBudgetItemTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSaveBudgetItemTags = async (budgetItemId: number) => {
    if (selectedBudgetItemTags.length === 0) {
      toast.warning('Please select at least one tag');
      return;
    }

    try {
      setSavingBudgetItemTags(true);
      const response = await fetchWithAuth(
        apiUrl(`diagnostics/missing-tags/budget-items/${budgetItemId}/tags`),
        {
          method: 'POST',
          body: JSON.stringify({ tagIds: selectedBudgetItemTags }),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(result.message);
        setEditingBudgetItemRow(null);
        setSelectedBudgetItemTags([]);
        await fetchBudgetItemsData();
      } else {
        toast.error('Failed to add tags');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add tags');
    } finally {
      setSavingBudgetItemTags(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fi-FI');
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-center">
            <div className="text-gray-500">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="text-red-600">Error: {error}</div>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Missing Tags Diagnostics</h1>
            <p className="text-sm text-gray-500 mt-1">Costpoint assignments without any tags</p>
          </div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center">
          <div className="text-4xl font-bold text-orange-600">{data.length}</div>
          <div className="text-sm text-gray-500 mt-1">Costpoint assignments without tags</div>
        </div>
      </div>

      {/* Data Table */}
      {data.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="text-center text-gray-500">✓ All costpoint assignments have tags!</div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Costpoint
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((item) => (
                  <React.Fragment key={item.costpoint_assignment_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.costpoint_assignment_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <a
                          href={`/transactions?id=${item.transaction_id}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {item.transaction_id}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.transaction_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {item.transaction_description || (
                          <span className="text-gray-400 italic">No description</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {item.costpoint_name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {formatAmount(item.allocated_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                        {editingRow === item.costpoint_assignment_id ? (
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => handleSaveTags(item.costpoint_assignment_id)}
                              disabled={saving || selectedTags.length === 0}
                              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={saving}
                              className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 text-xs font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEditRow(item.costpoint_assignment_id)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium"
                          >
                            Add Tags
                          </button>
                        )}
                      </td>
                    </tr>
                    {editingRow === item.costpoint_assignment_id && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-gray-50">
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-700">
                              Select tags to add:
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {tags.map((tag) => (
                                <button
                                  key={tag.id}
                                  onClick={() => toggleTag(tag.id)}
                                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    selectedTags.includes(tag.id)
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  {tag.display_name}
                                </button>
                              ))}
                            </div>
                            {selectedTags.length > 0 && (
                              <div className="text-sm text-gray-600">
                                Selected: {selectedTags.length} tag(s)
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">About This Page</h3>
        <p className="text-sm text-blue-700">
          This diagnostic page shows all costpoint assignments that don't have any tags assigned.
          Click "Add Tags" on any row to select and add tags directly. The item will be removed from
          this list once tags are added.
        </p>
      </div>

      {/* Budget Items Without Tags - admin/superadmin/budget_creator only */}
      {canManageBudgetTags && (
        <>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Budget Items Without Tags</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Budget items (any period, any status) that have no tags assigned
                </p>
              </div>
              <button
                onClick={fetchBudgetItemsData}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                data-testid="refresh-budget-items-button"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600">{budgetItemsData.length}</div>
              <div className="text-sm text-gray-500 mt-1">Budget items without tags</div>
            </div>
          </div>

          {budgetItemsLoading ? (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-center">
                <div className="text-gray-500">Loading...</div>
              </div>
            </div>
          ) : budgetItemsError ? (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="text-red-600">Error: {budgetItemsError}</div>
              <button
                onClick={fetchBudgetItemsData}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : budgetItemsData.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="text-center text-gray-500">✓ All budget items have tags!</div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" data-testid="budget-items-missing-tags-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Period
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Costpoint
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {budgetItemsData.map((item) => (
                      <React.Fragment key={item.budget_item_id}>
                        <tr className="hover:bg-gray-50" data-testid={`budget-item-row-${item.budget_item_id}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              <span>
                                {item.year} {item.period_type}
                              </span>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PERIOD_STATUS_BADGE_STYLES[item.period_status]}`}
                              >
                                {item.period_status}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {item.costpoint}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                            {item.type}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {item.description || (
                              <span className="text-gray-400 italic">No description</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                            {formatAmount(item.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                            {editingBudgetItemRow === item.budget_item_id ? (
                              <div className="flex items-center justify-center space-x-2">
                                <button
                                  onClick={() => handleSaveBudgetItemTags(item.budget_item_id)}
                                  disabled={savingBudgetItemTags || selectedBudgetItemTags.length === 0}
                                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                                >
                                  {savingBudgetItemTags ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={handleCancelBudgetItemEdit}
                                  disabled={savingBudgetItemTags}
                                  className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 text-xs font-medium"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleEditBudgetItemRow(item.budget_item_id)}
                                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium"
                                data-testid={`add-tags-budget-item-${item.budget_item_id}`}
                              >
                                Add Tags
                              </button>
                            )}
                          </td>
                        </tr>
                        {editingBudgetItemRow === item.budget_item_id && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 bg-gray-50">
                              <div className="space-y-2">
                                <div className="text-sm font-medium text-gray-700">
                                  Select tags to add:
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {tags.map((tag) => (
                                    <button
                                      key={tag.id}
                                      onClick={() => toggleBudgetItemTag(tag.id)}
                                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                        selectedBudgetItemTags.includes(tag.id)
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                      }`}
                                    >
                                      {tag.display_name}
                                    </button>
                                  ))}
                                </div>
                                {selectedBudgetItemTags.length > 0 && (
                                  <div className="text-sm text-gray-600">
                                    Selected: {selectedBudgetItemTags.length} tag(s)
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">About This Section</h3>
            <p className="text-sm text-blue-700">
              Shows budget items (expenses or income, any period, including locked ones) that
              have no tags assigned. Adding a tag here doesn't change the item's amount,
              description, or the budget period's lock status - only admins and budget managers
              can see and use this section.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default MissingTags;
